import { ChangeEvent } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import IconButton from "@mui/material/IconButton";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import AmountInput from "./AmountInput";
import { FIXED_EXPENSE_FIELDS } from "./transfer.utils";
import {
  AdditionalExpenseDraft,
  additionalExpenseErrorKey,
  ExpenseMoneyDraft,
  ExpenseMoneyField,
} from "./useExpenseDraft";

interface ExpenseEditFieldsProps {
  money: ExpenseMoneyDraft;
  additionalExpenses: AdditionalExpenseDraft[];
  /** Keyed by money field, and by `additionalExpenseErrorKey` for the free-form rows. */
  errors: Record<string, string>;
  disabled: boolean;
  onChangeMoney: (field: ExpenseMoneyField, value: string) => void;
  onAddExpense: () => void;
  onChangeExpense: (
    id: string,
    patch: Partial<Pick<AdditionalExpenseDraft, "description" | "amount">>,
  ) => void;
  onRemoveExpense: (id: string) => void;
}

/**
 * The แก้ไขค่าใช้จ่าย form: the five fixed buckets, the free-form รายการเพิ่มเติม list, and
 * ยอดฝากเก็บ.
 *
 * Laid out in the same order as the แจ้งยอดโอน form the branch originally filed on, so a
 * figure being corrected is found in the place it was typed. ยอดฝากเก็บ is kept below a
 * divider because it is not an expense — it is added to the remit, not subtracted from it,
 * and it is deliberately outside the รวมค่าใช้จ่าย the footer previews.
 */
export default function ExpenseEditFields({
  money,
  additionalExpenses,
  errors,
  disabled,
  onChangeMoney,
  onAddExpense,
  onChangeExpense,
  onRemoveExpense,
}: ExpenseEditFieldsProps) {
  // Curried so each field binds its own key without an inline arrow in JSX.
  const changeMoney = (field: ExpenseMoneyField) => (value: string) =>
    onChangeMoney(field, value);

  const changeDescription =
    (id: string) => (event: ChangeEvent<HTMLInputElement>) =>
      onChangeExpense(id, { description: event.target.value });

  const changeAmount = (id: string) => (amount: string) =>
    onChangeExpense(id, { amount });

  const removeExpense = (id: string) => () => onRemoveExpense(id);

  return (
    <Stack gap={2} sx={{ py: 1 }}>
      {FIXED_EXPENSE_FIELDS.map((field) => (
        <AmountInput
          key={field.key}
          label={field.label}
          value={money[field.key]}
          error={errors[field.key]}
          disabled={disabled}
          onChange={changeMoney(field.key)}
        />
      ))}

      {additionalExpenses.map((expense, index) => (
        <Box
          key={expense.id}
          sx={{
            display: "grid",
            gridTemplateColumns: {
              xs: "minmax(0, 1fr) auto",
              sm: "minmax(0, 1fr) 180px auto",
            },
            gap: 1,
            alignItems: "start",
          }}
        >
          <TextField
            label={`รายการเพิ่มเติม ${index + 1}`}
            value={expense.description}
            onChange={changeDescription(expense.id)}
            size="small"
            fullWidth
            disabled={disabled}
            error={Boolean(
              errors[additionalExpenseErrorKey(expense.id, "description")],
            )}
            helperText={
              errors[additionalExpenseErrorKey(expense.id, "description")]
            }
            sx={{ gridColumn: { xs: "1 / -1", sm: "auto" } }}
          />
          <AmountInput
            label="จำนวนเงิน"
            value={expense.amount}
            error={errors[additionalExpenseErrorKey(expense.id, "amount")]}
            disabled={disabled}
            onChange={changeAmount(expense.id)}
          />
          <IconButton
            aria-label={`ลบรายการเพิ่มเติม ${index + 1}`}
            color="error"
            disabled={disabled}
            onClick={removeExpense(expense.id)}
            sx={{ mt: 0.25 }}
          >
            <DeleteOutlineIcon />
          </IconButton>
        </Box>
      ))}

      <Button
        variant="outlined"
        startIcon={<AddIcon />}
        onClick={onAddExpense}
        disabled={disabled}
        sx={{ alignSelf: "flex-end" }}
      >
        เพิ่มรายการค่าใช้จ่าย
      </Button>

      <Divider />

      <AmountInput
        label="ยอดฝากเก็บ"
        value={money.deposit_collection_amount}
        error={errors.deposit_collection_amount}
        disabled={disabled}
        onChange={changeMoney("deposit_collection_amount")}
      />
      <Typography variant="caption" color="text.secondary">
        ยอดฝากเก็บไม่ใช่ค่าใช้จ่าย แต่ถูกบวกเข้ากับยอดที่ต้องโอน
      </Typography>
    </Stack>
  );
}
